/// <reference types="vite/client" />
// ─────────────────────────────────────────────────────
// API service — HTTP client for SXT Platform
// ─────────────────────────────────────────────────────
import axios from "axios";

// In production on Pages: /api/v1 routes through the edge proxy function (no CORS).
// Locally: hit Cloud Run directly.
const BASE_URL =
  import.meta.env.VITE_API_URL ||
  (window.location.hostname === "localhost"
    ? "https://sxt-platform-705725624713.us-central1.run.app/api/v1"
    : "/api/v1");

export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
  headers: { "Content-Type": "application/json" },
});

// Token interceptor
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("sxt_token");
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 interceptor — auto-logout on expired/invalid token
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("sxt_token");
      localStorage.removeItem("sxt_refresh_token");
      localStorage.removeItem("sxt_user_id");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// API methods
export const api = {
  // Auth
  login: (email: string, password: string) =>
    apiClient.post("/auth/login", { email, password }).then((r) => r.data),

  // Identity
  getProfile: () => apiClient.get("/identity/profile").then((r) => r.data),
  updateProfile: (data: Record<string, unknown>) =>
    apiClient.put("/identity/profile", data).then((r) => r.data),

  // Wardrobe
  getWardrobe: (category?: string) =>
    apiClient.get("/style/wardrobe", { params: category ? { category } : {} }).then((r) => r.data),
  getGarment: (id: string) =>
    apiClient.get(`/style/wardrobe/${id}`).then((r) => r.data),
  addGarment: (data: { name: string; category: string; color?: string }) =>
    apiClient.post("/style/wardrobe", data).then((r) => r.data),

  // Style
  getRecommendations: () => apiClient.get("/style/recommendations").then((r) => r.data),
  getStyleProfile: () => apiClient.get("/style/profile").then((r) => r.data),

  // Analytics
  getAnalytics: (period?: string) =>
    apiClient.get("/identity/analytics", { params: period ? { period } : {} }).then((r) => r.data),

  // Social
  getPlatforms: () => apiClient.get("/social/accounts").then((r) => r.data),
  getSharedPosts: () => apiClient.get("/social/history").then((r) => r.data),
  connectPlatform: (platform: string, authCode: string, redirectUri: string) =>
    apiClient.post("/social/connect", { platform, auth_code: authCode, redirect_uri: redirectUri }).then((r) => r.data),
  shareContent: (data: { platform: string; content_type: string; media_url: string; caption?: string }) =>
    apiClient.post("/social/share", data).then((r) => r.data),

  // Try-On
  startTryOn: (garmentId: string) =>
    apiClient.post("/tryon/start", { garment_id: garmentId }).then((r) => r.data),
  getTryOnHistory: () => apiClient.get("/tryon/history").then((r) => r.data),

  // Commerce
  getProducts: (query?: string) =>
    apiClient.get("/commerce/products", { params: query ? { query } : {} }).then((r) => r.data),
  getCart: () => apiClient.get("/commerce/cart").then((r) => r.data),
  addToCart: (productId: string, quantity: number = 1) =>
    apiClient.post("/commerce/cart/add", { product_id: productId, quantity }).then((r) => r.data),
  checkout: (paymentMethodId: string, shippingAddress: Record<string, string>) =>
    apiClient.post("/commerce/checkout", { payment_method_id: paymentMethodId, shipping_address: shippingAddress }).then((r) => r.data),

  // BCI
  getBCIEmotion: () => apiClient.get("/bci/emotion").then((r) => r.data),
  getBCIStatus: () => apiClient.get("/bci/stream/status").then((r) => r.data),

  // Vision
  startBodyScan: (sessionId: string) =>
    apiClient.post("/vision/scan", { session_id: sessionId }).then((r) => r.data),
  getMeasurements: () => apiClient.get("/vision/measurements").then((r) => r.data),

  // Token refresh
  refreshToken: (refreshToken: string) =>
    apiClient.post("/auth/refresh", { refresh_token: refreshToken }).then((r) => r.data),

  // Register
  register: (email: string, username: string, password: string) =>
    apiClient.post("/auth/register", { email, username, password }).then((r) => r.data),

  // Identity — settings
  getMe: () => apiClient.get("/identity/me").then((r) => r.data),
  updatePreferences: (data: Record<string, unknown>) =>
    apiClient.patch("/identity/preferences", data).then((r) => r.data),

  // Mirror
  getMirrorStatus: () => apiClient.get("/identity/mirror/status").then((r) => r.data),
};
